import React, { useEffect, useRef, useState } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from './firebase';
import { obtenerMiPerfil } from './api/perfil';
import { useNavigate, useLocation } from 'react-router-dom';
import './Login.css';

// ── Scroll reveal hook ───────────────────────────────────────
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.lp-reveal');

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            observer.unobserve(e.target);
          }
        }),
      { threshold: 0.15 }
    );

    els.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
}

// ── Feature sections data ────────────────────────────────────
const SECTIONS = [
  {
    tag: 'Precios en tiempo real',
    tagClass: 'lp-tag-blue',
    heading: (
      <>
        Todos los precios,
        <br />
        <em>siempre actualizados.</em>
      </>
    ),
    paragraphs: [
      <>
        Cada lunes el sistema recorre el catálogo completo del mercado chileno y
        registra los precios actualizados de <strong>más de 1.500 versiones</strong>{' '}
        en 40+ marcas. Sin trabajo manual, sin datos desactualizados.
      </>,
      <>
        Cada modelo puede tener hasta 4 tipos de precio —{' '}
        <strong>
          Precio de lista, Todo medio de pago, Crédito convencional y Crédito
          inteligente
        </strong>{' '}
        — y todos quedan registrados con su fecha exacta para construir el
        historial.
      </>,
    ],
    bullets: [
      {
        icon: '📅',
        color: '#1e3a5f',
        text: 'Historial completo con evolución de precio por tipo de financiamiento',
      },
      {
        icon: '📊',
        color: '#1e3a5f',
        text: 'Gráficos de evolución para detectar tendencias y estacionalidad',
      },
      {
        icon: '🔔',
        color: '#1e3a5f',
        text: 'Sección "Novedades" que destaca automáticamente los mayores movimientos de precio de la semana',
      },
    ],
    mockTitle: 'Catálogo · Toyota',
    mockContent: 'table-precios',
    reverse: false,
    alt: false,
  },
  {
    tag: 'Asesor con IA',
    tagClass: 'lp-tag-gold',
    heading: (
      <>
        <em>Pregunta</em> como
        <br />
        le preguntarías
        <br />a un experto.
      </>
    ),
    paragraphs: [
      <>
        El asesor entiende lenguaje natural. Dile{' '}
        <strong>
          "busco un SUV familiar por menos de 25 millones que no sea chino"
        </strong>{' '}
        y te entregará una comparativa completa con todos los tipos de precio,
        especificaciones técnicas y análisis de valor de reventa.
      </>,
      <>
        No inventa datos. Trabaja exclusivamente con la información real de la
        base de datos — precios actualizados, fichas técnicas y publicaciones de
        usados — y te presenta un veredicto justificado con números concretos.
      </>,
    ],
    bullets: [
      {
        icon: '🎯',
        color: '#3b2a08',
        text: 'Detecta presupuesto, segmento, marca y origen desde el texto libre',
      },
      {
        icon: '🌍',
        color: '#3b2a08',
        text: 'Filtros por origen ("no chino", "que sea japonés") y categoría ("SUV", "sedan")',
      },
      {
        icon: '📋',
        color: '#3b2a08',
        text: 'Respuesta estructurada: precios → specs → valor de reventa → veredicto',
      },
    ],
    mockTitle: 'Asesor DoctorCar',
    mockContent: 'chat',
    reverse: true,
    alt: true,
  },
  {
    tag: 'Comparador',
    tagClass: 'lp-tag-purple',
    heading: (
      <>
        Compara hasta
        <br />
        <em>6 modelos</em>
        <br />
        lado a lado.
      </>
    ),
    paragraphs: [
      <>
        Selecciona cualquier combinación de modelos y obtienes una tabla
        unificada con todos los tipos de precio por modelo, destacando cuál
        ofrece el <strong>precio más bajo en cada modalidad</strong> de
        financiamiento.
      </>,
      <>
        Más abajo, la comparativa técnica cruza motor, dimensiones, suspensión y
        rendimiento. Y al final, el equipamiento completo — seguridad,
        multimedia, confort, ADAS — con{' '}
        <strong>las diferencias resaltadas en amarillo</strong> para que no se
        te escape nada.
      </>,
    ],
    bullets: [
      {
        icon: '💛',
        color: '#2a2010',
        text: 'Filas amarillas destacan diferencias de equipamiento entre modelos',
      },
      {
        icon: '⚙️',
        color: '#2a2010',
        text: 'Motor, dimensiones, suspensión, rendimiento y capacidades en una sola vista',
      },
      {
        icon: '🛡',
        color: '#2a2010',
        text: 'Equipamiento: Seguridad, ADAS, Multimedia, Confort, Interior y Exterior',
      },
    ],
    mockTitle: 'Comparador · 3 modelos',
    mockContent: 'comparador',
    reverse: false,
    alt: false,
  },
  {
    tag: 'Mercado de usados',
    tagClass: 'lp-tag-green',
    heading: (
      <>
        ¿Cuánto pierde
        <br />
        de valor un <em>auto</em>
        <br />
        con el tiempo?
      </>
    ),
    paragraphs: [
      <>
        La sección de usados cruza las publicaciones activas del mercado con los
        modelos nuevos del catálogo. Para cada modelo puedes ver el rango de
        precios de segunda mano, el promedio de kilometraje y los años
        disponibles.
      </>,
      <>
        El sistema calcula la <strong>depreciación real</strong> mediante
        regresión multivariable sobre precio, edad y kilometraje — no
        estimaciones genéricas, sino el comportamiento real del modelo
        específico en el mercado chileno.
      </>,
    ],
    bullets: [
      {
        icon: '📉',
        color: '#0f3020',
        text: 'Depreciación anual en CLP y porcentaje, calculada con datos reales de mercado',
      },
      {
        icon: '🔢',
        color: '#0f3020',
        text: 'Costo por kilómetro de depreciación para comparar modelos de manera justa',
      },
      {
        icon: '📈',
        color: '#0f3020',
        text: 'Gráficos de precio promedio y kilometraje por año del modelo',
      },
    ],
    mockTitle: 'Usados · Análisis',
    mockContent: 'usados',
    reverse: true,
    alt: true,
  },
  {
    tag: 'Mis proyectos y análisis PM',
    tagClass: 'lp-tag-teal',
    heading: (
      <>
        Crea listas,
        <br />
        <em>analiza</em> tu
        <br />
        competencia.
      </>
    ),
    paragraphs: [
      <>
        Guarda modelos en listas personalizadas para hacer seguimiento de tu
        segmento o de la competencia. Selecciona 2 o más modelos y activa el{' '}
        <strong>análisis estratégico con IA</strong> — un reporte de
        posicionamiento precio/specs, gaps de mercado y recomendaciones de
        pricing.
      </>,
      <>
        Diseñado para equipos de producto y marketing de marcas automotrices que
        necesitan <strong>inteligencia competitiva accionable</strong> basada en
        datos reales del mercado chileno.
      </>,
    ],
    bullets: [
      {
        icon: '🗂',
        color: '#0a2a2a',
        text: 'Listas ilimitadas, cada una con descripción y seguimiento de modelos',
      },
      {
        icon: '🤖',
        color: '#0a2a2a',
        text: 'Análisis PM con Claude: posicionamiento, gaps y estrategia de pricing',
      },
      {
        icon: '📤',
        color: '#0a2a2a',
        text: 'Exporta a Excel los modelos de tu lista para análisis externo',
      },
    ],
    mockTitle: 'Mi lista · Análisis PM',
    mockContent: 'listas',
    reverse: false,
    alt: false,
  },
];

// ── Mock components ──────────────────────────────────────────
function MockTablaPrecios() {
  return (
    <div className="lp-mock">
      <div className="lp-mock-bar">
        <div className="lp-mock-d" style={{ background: '#ef4444' }} />
        <div className="lp-mock-d" style={{ background: '#f59e0b' }} />
        <div className="lp-mock-d" style={{ background: '#22c55e' }} />
        <span className="lp-mock-ttl">Catálogo DoctorCar</span>
      </div>

      <div className="lp-mock-body">
        <table className="lp-tbl">
          <thead>
            <tr>
              <th>Marca</th>
              <th>Modelo</th>
              <th>Versión</th>
              <th>Lista</th>
              <th>Crédito</th>
              <th>Δ</th>
            </tr>
          </thead>

          <tbody>
            {[
              [
                'Toyota',
                'RAV4',
                '2.5 Hybrid',
                '$37.490.000',
                '$33.900.000',
                <span className="lp-badge lp-badge-up">+$1.2M</span>,
              ],
              [
                'Hyundai',
                'Tucson',
                '1.6T Premium',
                '$28.990.000',
                '$25.490.000',
                <span className="lp-badge lp-badge-dn">−$800K</span>,
              ],
              [
                'BYD',
                'Seal',
                'EV AWD',
                '$30.490.000',
                '$27.990.000',
                <span className="lp-badge lp-badge-dn">−$2.5M</span>,
              ],
              [
                'Mazda',
                'CX-5',
                '2.5 Skyactiv',
                '$31.990.000',
                '$28.490.000',
                <span className="lp-badge lp-badge-up">+$500K</span>,
              ],
              [
                'Kia',
                'Sportage',
                'HEV AWD',
                '$29.990.000',
                '$26.500.000',
                '—',
              ],
            ].map(([marca, model, ver, lista, cred, delta], i) => (
              <tr key={i}>
                <td>
                  <span className="lp-marca">{marca}</span>
                </td>
                <td style={{ fontWeight: 500, color: '#edf0f3' }}>{model}</td>
                <td style={{ color: '#4a5568', fontSize: 10 }}>{ver}</td>
                <td style={{ fontWeight: 600, color: '#edf0f3' }}>{lista}</td>
                <td>{cred}</td>
                <td>{delta}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MockChat() {
  return (
    <div className="lp-mock">
      <div className="lp-mock-bar">
        <div className="lp-mock-d" style={{ background: '#ef4444' }} />
        <div className="lp-mock-d" style={{ background: '#f59e0b' }} />
        <div className="lp-mock-d" style={{ background: '#22c55e' }} />
        <span className="lp-mock-ttl">Asesor DoctorCar</span>
      </div>

      <div className="lp-mock-body">
        <div className="lp-chat">
          <div className="lp-bubble lp-bubble-user">
            Busco un SUV para familia, presupuesto 28 millones, no quiero autos
            chinos
          </div>

          <div className="lp-bubble lp-bubble-ai">
            <strong style={{ color: '#93c5fd' }}>
              Encontré 8 SUV dentro de tu presupuesto
            </strong>{' '}
            (Japoneses, Coreanos y Europeos):
            <br />
            <br />
            🥇{' '}
            <strong style={{ color: '#edf0f3' }}>Toyota RAV4 2.5 HEV</strong> —
            mejor retención de valor (7.1% dep/año)
            <br />
            🥈{' '}
            <strong style={{ color: '#edf0f3' }}>Hyundai Tucson 1.6T</strong> —
            mejor equipamiento de serie
            <br />
            🥉 <strong style={{ color: '#edf0f3' }}>Mazda CX-5 AWD</strong> —
            mejor manejo, menor consumo
            <br />
            <br />
            Con crédito inteligente, los tres entran a tu presupuesto. ¿Quieres
            la comparativa completa?
          </div>

          <div className="lp-bubble lp-bubble-user">
            Sí, muéstrame precios y specs
          </div>

          <div
            className="lp-bubble lp-bubble-ai"
            style={{ padding: '10px 14px' }}
          >
            <div className="lp-typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MockComparador() {
  return (
    <div className="lp-mock">
      <div className="lp-mock-bar">
        <div className="lp-mock-d" style={{ background: '#ef4444' }} />
        <div className="lp-mock-d" style={{ background: '#f59e0b' }} />
        <div className="lp-mock-d" style={{ background: '#22c55e' }} />
        <span className="lp-mock-ttl">
          Comparador · RAV4 vs Tucson vs CX-5
        </span>
      </div>

      <div className="lp-mock-body">
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 10,
              color: '#3d4452',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 8,
            }}
          >
            Precios
          </div>

          {[
            ['Precio de lista', '$37.490.000', '$28.990.000', '$31.990.000'],
            ['Crédito inteligente', '$33.900.000', '$25.490.000', '$28.490.000'],
            ['Todo medio de pago', '$35.200.000', '$27.200.000', '$30.100.000'],
          ].map(([tipo, a, b, c], i) => (
            <div
              key={i}
              className="lp-comp-row"
              style={{ gridTemplateColumns: '1fr 80px 80px 80px' }}
            >
              <span style={{ fontSize: 10, color: '#7c8494' }}>{tipo}</span>
              <span
                style={{
                  fontSize: 10,
                  color: i === 1 ? '#4ade80' : '#edf0f3',
                  fontWeight: i === 1 ? 700 : 400,
                }}
              >
                {a}
              </span>
              <span style={{ fontSize: 10, color: '#edf0f3' }}>{b}</span>
              <span style={{ fontSize: 10, color: '#edf0f3' }}>{c}</span>
            </div>
          ))}
        </div>

        <div>
          <div
            style={{
              fontSize: 10,
              color: '#3d4452',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 8,
            }}
          >
            Equipamiento
          </div>

          {[
            ['Pantalla 10"', '✓', '✓', '✓'],
            ['Cámara 360°', '✓', '✓', '✗'],
            ['Head-up display', '✓', '✗', '✗'],
            ['ADAS completo', '✓', '✓', '✗'],
          ].map(([feat, a, b, c], i) => {
            const hasDiff = [a, b, c].some((v) => v === '✗');

            return (
              <div
                key={i}
                className="lp-comp-row"
                style={{
                  gridTemplateColumns: '1fr 80px 80px 80px',
                  background: hasDiff ? 'rgba(240,180,41,0.04)' : undefined,
                }}
              >
                <span style={{ fontSize: 10, color: '#7c8494' }}>{feat}</span>

                {[a, b, c].map((v, j) => (
                  <span
                    key={j}
                    style={{
                      fontSize: 11,
                      color: v === '✓' ? '#4ade80' : '#3d4452',
                      fontWeight: 600,
                    }}
                  >
                    {v}
                  </span>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MockUsados() {
  const bars = [
    { year: '2019', pct: 45, price: '$14.2M' },
    { year: '2020', pct: 55, price: '$16.8M' },
    { year: '2021', pct: 68, price: '$19.5M' },
    { year: '2022', pct: 82, price: '$23.1M' },
    { year: '2023', pct: 92, price: '$27.4M' },
  ];

  return (
    <div className="lp-mock">
      <div className="lp-mock-bar">
        <div className="lp-mock-d" style={{ background: '#ef4444' }} />
        <div className="lp-mock-d" style={{ background: '#f59e0b' }} />
        <div className="lp-mock-d" style={{ background: '#22c55e' }} />
        <span className="lp-mock-ttl">Usados · Toyota RAV4 Hybrid</span>
      </div>

      <div className="lp-mock-body">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 8,
            marginBottom: 20,
          }}
        >
          {[
            { lbl: 'Publicaciones', val: '47' },
            { lbl: 'Precio mediana', val: '$21.5M' },
            { lbl: 'Dep. anual', val: '7.1%' },
          ].map(({ lbl, val }) => (
            <div
              key={lbl}
              style={{
                background: 'rgba(255,255,255,0.04)',
                borderRadius: 8,
                padding: '10px 12px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 9,
                  color: '#3d4452',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 4,
                }}
              >
                {lbl}
              </div>

              <div
                style={{ fontSize: 14, fontWeight: 700, color: '#edf0f3' }}
              >
                {val}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            fontSize: 10,
            color: '#3d4452',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 10,
          }}
        >
          Precio promedio por año
        </div>

        {bars.map(({ year, pct, price }) => (
          <div className="lp-bar-row" key={year}>
            <span className="lp-bar-lbl">{year}</span>

            <div className="lp-bar-wrap">
              <div
                className="lp-bar"
                style={{
                  width: `${pct}%`,
                  background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)',
                }}
              />
            </div>

            <span
              style={{
                fontSize: 10,
                color: '#7c8494',
                width: 50,
                flexShrink: 0,
                textAlign: 'right',
              }}
            >
              {price}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockListas() {
  return (
    <div className="lp-mock">
      <div className="lp-mock-bar">
        <div className="lp-mock-d" style={{ background: '#ef4444' }} />
        <div className="lp-mock-d" style={{ background: '#f59e0b' }} />
        <div className="lp-mock-d" style={{ background: '#22c55e' }} />
        <span className="lp-mock-ttl">Análisis PM · SUVs Medianos</span>
      </div>

      <div className="lp-mock-body">
        <div
          style={{
            fontSize: 10,
            color: '#3d4452',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 12,
          }}
        >
          Modelos en lista
        </div>

        {[
          'Toyota RAV4 Hybrid',
          'Hyundai Tucson 1.6T',
          'Mazda CX-5 AWD',
          'Kia Sportage HEV',
        ].map((m, i) => {
          const colors = ['#3b82f6', '#0891b2', '#7c3aed', '#059669'];

          return (
            <div
              key={m}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: colors[i],
                  flexShrink: 0,
                }}
              />

              <span style={{ fontSize: 11, color: '#7c8494' }}>{m}</span>
            </div>
          );
        })}

        <div
          style={{
            marginTop: 16,
            padding: '12px',
            background: 'rgba(59,130,246,0.06)',
            border: '1px solid rgba(59,130,246,0.15)',
            borderRadius: 8,
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: '#60a5fa',
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            💡 Análisis PM — Claude
          </div>

          <div style={{ fontSize: 11, color: '#7c8494', lineHeight: 1.5 }}>
            RAV4 lidera en valor de reventa pero tiene el precio más alto.{' '}
            <strong style={{ color: '#93c5fd' }}>Gap de oportunidad</strong>:
            Tucson ofrece equipamiento comparable con un 22% menos de precio...
          </div>
        </div>
      </div>
    </div>
  );
}

const MOCK_MAP = {
  'table-precios': <MockTablaPrecios />,
  chat: <MockChat />,
  comparador: <MockComparador />,
  usados: <MockUsados />,
  listas: <MockListas />,
};

// ── Main component ───────────────────────────────────────────
function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/modelos';

  useReveal();

  const handleLogin = async (e) => {
    e.preventDefault();

    setError('');
    setLoading(true);

    try {
      // 1. Login Firebase Auth
      const credential = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );

      const user = credential.user;

      // 2. Buscar perfil del usuario (Postgres/Neon, vía la misma función
      // que usa useUserRole en el resto de la app — una sola fuente de
      // verdad para el rol, ya no Firestore).
      const userData = await obtenerMiPerfil();
      const rol = String(userData?.role || '').trim().toLowerCase();

      console.log('Usuario autenticado:', {
        email: user.email,
        uid: user.uid,
        rol,
      });

      // 3. Roles válidos del sistema
      const rolesPermitidos = [
        'basico',
        'intermedio',
        'completo',
        'superadmin',
      ];

      if (!rolesPermitidos.includes(rol)) {
        console.warn('Usuario sin rol permitido:', {
          email: user.email,
          uid: user.uid,
          rol,
        });

        await signOut(auth);

        setError(
          'Tu cuenta no tiene acceso habilitado a DoctorCar. Contacta al administrador.'
        );

        return;
      }

      // 4. Login correcto
      if (onLogin) {
        onLogin({
          uid: user.uid,
          email: user.email,
          rol,
          ...userData,
        });
      }

      navigate(from, { replace: true });
    } catch (err) {
      console.error('Error en login:', err);

      if (
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/wrong-password' ||
        err.code === 'auth/user-not-found'
      ) {
        setError(
          'Correo o contraseña incorrectos. Verifica tus credenciales.'
        );
      } else if (err.code === 'auth/too-many-requests') {
        setError(
          'Demasiados intentos. Espera unos minutos e intenta nuevamente.'
        );
      } else if (err.code === 'permission-denied' || err.code === 'unauthenticated') {
        setError(
          'No fue posible validar tu perfil. Contacta al administrador.'
        );
      } else {
        setError(
          'No fue posible iniciar sesión. Intenta nuevamente.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        background: 'var(--bg)',
        fontFamily: "'DM Sans', sans-serif",
        color: 'var(--text-1)',
      }}
    >
      {/* ── Nav ── */}
      <nav className="lp-nav">
        <a className="lp-nav-logo" href="#hero">
          <img
            src="/logo_doctorcar.png"
            alt="DoctorCar"
            className="lp-nav-logo-img"
          />

          <span className="lp-nav-logo-name">
            Doctor<span>Car</span>
          </span>
        </a>

        <ul className="lp-nav-links">
          <li>
            <a href="#precios">Precios</a>
          </li>
          <li>
            <a href="#asesor">Asesor IA</a>
          </li>
          <li>
            <a href="#comparador">Comparador</a>
          </li>
          <li>
            <a href="#usados">Usados</a>
          </li>
          <li>
            <a href="#login" className="lp-nav-cta">
              Ingresar
            </a>
          </li>
        </ul>
      </nav>

      {/* ── Hero ── */}
      <section id="hero" className="lp-hero">
        <div className="lp-eyebrow">
          <span className="lp-pulse-dot" />
          Mercado automotriz chileno
        </div>

        <h1 className="lp-h1">
          Inteligencia
          <br />
          <em>para decidir</em>
          <span className="sub-line">mejor.</span>
        </h1>

        <p className="lp-hero-p">
          Catálogo de vehículos con precios en tiempo real, comparativas
          técnicas, análisis de mercado de usados y un asesor con IA entrenado
          en el mercado chileno.
        </p>

        <div className="lp-hero-actions">
          <a href="#login" className="lp-btn-primary">
            Comenzar ahora
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M3 7h8M8 4l3 3-3 3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>

          <a href="#precios" className="lp-btn-ghost">
            Ver funcionalidades ↓
          </a>
        </div>

        <div className="lp-stats">
          {[
            ['40+', 'Marcas'],
            ['1.500+', 'Modelos'],
            ['Semanal', 'Actualización'],
            ['IA', 'Asesor integrado'],
          ].map(([val, lbl]) => (
            <div className="lp-stat" key={lbl}>
              <div className="lp-stat-val">{val}</div>
              <div className="lp-stat-lbl">{lbl}</div>
            </div>
          ))}
        </div>

        <div className="lp-scroll-hint">
          <span>Scroll</span>

          <div className="lp-scroll-arrow">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M2 4l3 3 3-3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </section>

      {/* ── Feature sections ── */}
      {SECTIONS.map((sec, idx) => (
        <section
          key={idx}
          id={['precios', 'asesor', 'comparador', 'usados', 'listas'][idx]}
          className={`lp-section${sec.alt ? ' lp-section-alt' : ''}`}
        >
          <div className={`lp-section-inner${sec.reverse ? ' rev' : ''}`}>
            <div>
              <div className={`lp-tag ${sec.tagClass} lp-reveal`}>
                {sec.tag}
              </div>

              <h2 className="lp-section-h lp-reveal lp-d1">
                {sec.heading}
              </h2>

              {sec.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className={`lp-section-p lp-reveal lp-d${i + 2}`}
                >
                  {p}
                </p>
              ))}

              <ul className="lp-bullets">
                {sec.bullets.map((b, i) => (
                  <li
                    key={i}
                    className={`lp-bullet lp-reveal lp-d${i + 3}`}
                  >
                    <div
                      className="lp-bullet-icon"
                      style={{ background: b.color, fontSize: 11 }}
                    >
                      {b.icon}
                    </div>

                    <span>{b.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lp-reveal lp-d2">
              {MOCK_MAP[sec.mockContent]}
            </div>
          </div>
        </section>
      ))}

      {/* ── Login ── */}
      <section id="login" className="lp-login-section">
        <div className="lp-login-box lp-reveal">
          <div className="lp-login-pre">
            <h2>Accede a DoctorCar</h2>

            <p>
              Ingresa con tu cuenta para acceder al catálogo completo, el
              asesor con IA y todas las herramientas.
            </p>
          </div>

          <div className="lp-login-card">
            <form className="lp-form" onSubmit={handleLogin}>
              <div className="lp-field">
                <label className="lp-label">
                  Correo electrónico
                </label>

                <input
                  type="email"
                  className="lp-input"
                  placeholder="tu@correo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="lp-field">
                <label className="lp-label">
                  Contraseña
                </label>

                <input
                  type="password"
                  className="lp-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>

              {error && (
                <div className="lp-error">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <circle
                      cx="7"
                      cy="7"
                      r="6"
                      stroke="#f87171"
                      strokeWidth="1.5"
                    />

                    <path
                      d="M7 4v3.5M7 9.5v.5"
                      stroke="#f87171"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>

                  {error}
                </div>
              )}

              <button
                type="submit"
                className="lp-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="lp-spinner" />
                    Ingresando…
                  </>
                ) : (
                  <>
                    Ingresar
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <path
                        d="M3 7h8M8 4l3 3-3 3"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="lp-footer-note">
            Plataforma de uso interno.
            <br />
            Contacta al administrador si no tienes acceso.
          </p>
        </div>
      </section>
    </div>
  );
}

export default Login;